import React, { ReactNode } from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import Grid from "veneer/Grid";
import List from "veneer/List";
import ListItem from "veneer/ListItem";
import Stack from "veneer/Stack";
import { Colors } from "veneer/Theme";
import Typography, { OverflowTooltipTypography } from "veneer/Typography";

import {
  DYNAMIC_CONTENT_TYPES,
  MEDIA_ENTRY_APPLICATION,
  MEDIA_ENTRY_CONTENT_GROUP,
  MEDIA_ENTRY_USER_CONTENT,
  MEDIA_ENTRY_WEBPAGE,
} from "cortex/ui/src/fleet/constant/mediaLibrary";
import type { ContentGroupsState } from "cortex/ui/src/fleet/redux-store/content-groups/reducers";
import type { Application } from "cortex/ui/src/fleet/view/application/model";
import { getPriorityLevel } from "cortex/ui/src/fleet/view/strategy/component/Util";
import { captureStack } from "cortex/ui/src/integrations/sentry";
import * as App from "cortex/ui/src/redux-store/applications/selectors";
import { getById } from "cortex/ui/src/redux-store/selectors";
import type { UserContentsState } from "cortex/ui/src/redux-store/user-contents/reducers";
import { devLogger } from "cortex/ui/src/util/logging";

import * as Sentry from "@sentry/browser";
import { StrategyDetailsStrategy, StrategySpot } from "../model";

type StrategyDetailsProps = {
  strategy: StrategyDetailsStrategy | null;
  strategyId: number;
  applications: { byId?: Record<string | number, Application> };
  contentGroups: ContentGroupsState;
  userContents: UserContentsState;
};

export default function StrategyDetails({
  strategy,
  strategyId,
  applications,
  contentGroups,
  userContents,
}: StrategyDetailsProps) {
  if (!strategy || !Array.isArray(strategy.priorities)) {
    return <div>N/A</div>;
  }

  const [main = [], fallback = []] = strategy.priorities;

  // Helpers ported from legacy view
  const isLegacyStrategy = (
    m: ReadonlyArray<StrategySpot>,
    f: ReadonlyArray<StrategySpot> = [],
  ) => [...m, ...f].some((s) => !s?.type);

  const getByApp = (appId?: number) => {
    const result: any = { canNotRun: false, itemValue: null };
    if (appId == null) {
      result.canNotRun = true;
      return result;
    }
    const app = getById(applications, appId);
    if (!app || !App.installed(app)) {
      result.canNotRun = true;
      return result;
    }
    result.itemValue = (
      <Link to={`/applications/${app.id}`}>{App.name(app)}</Link>
    );
    return result;
  };

  const getByContent = (contentId?: number) => {
    const result: any = {
      canNotRun: false,
      hasArchived: false,
      itemValue: null,
    };
    if (contentId == null) {
      result.canNotRun = true;
      return result;
    }
    const content = getById(userContents, contentId);
    if (!content) {
      result.canNotRun = true;
      return result;
    }
    result.itemValue = `${content.name} (Content)`;
    if (content.archived) result.hasArchived = true;
    return result;
  };

  const getByContentGroup = (contentGroupId?: number) => {
    const result: any = {
      canNotRun: false,
      hasArchived: false,
      itemValue: null,
    };
    if (contentGroupId == null) {
      result.canNotRun = true;
      return result;
    }
    const cg = getById(contentGroups, contentGroupId);
    if (!cg) {
      result.canNotRun = true;
      return result;
    }
    result.itemValue = `${cg.name} (Content Group)`;
    result.hasArchived = (cg.content || []).some((c) => {
      const content = getById(userContents, c.id);
      return Boolean(content?.archived);
    });
    return result;
  };

  const getByWebpage = (spot: StrategySpot) => ({
    itemValue:
      spot.url && spot.name ? (
        <Link to={spot.url}>{spot.name} (Web Page)</Link>
      ) : (
        "N/A"
      ),
  });

  const invalidSpotTypeWarning = (
    spot: StrategySpot,
    idx: number,
    spotIdx: number,
  ) => {
    const serialized = JSON.stringify(spot);
    const warning =
      "Unhandled strategy loop spot type " +
      `(${spot.type}). Strategy ${strategyId}, priority ${idx}, spot ${spotIdx} - ${serialized}`;
    devLogger(console.warn, warning, spot);
    captureStack();
    Sentry.captureMessage(warning, Sentry.Severity.Warning);
  };

  const formatSpots = (
    idx: number,
    priority: ReadonlyArray<StrategySpot> = [],
  ) => {
    if (idx === 1 && !priority) {
      return {
        canNotRun: false,
        hasArchived: false,
        hasDynamicContent: false,
        gridRows: [] as ReactNode[],
      };
    }

    let canNotRun = false;
    let hasArchived = false;
    const items: ReactNode[] = [];

    const hasDynamicContent = priority.some((spot) =>
      DYNAMIC_CONTENT_TYPES.includes(spot?.type ?? ""),
    );

    priority.forEach((spot, spotIdx) => {
      const { appId, contentId, contentGroupId, type } = spot || {};
      let result: any = {};

      switch (type) {
        case MEDIA_ENTRY_APPLICATION:
          result = getByApp(appId);
          break;
        case MEDIA_ENTRY_CONTENT_GROUP:
          result = getByContentGroup(contentGroupId ?? contentId);
          break;
        case MEDIA_ENTRY_USER_CONTENT:
          result = getByContent(contentId);
          break;
        case MEDIA_ENTRY_WEBPAGE:
          result = getByWebpage(spot);
          break;
        default:
          invalidSpotTypeWarning(spot, idx, spotIdx);
      }

      if (result.canNotRun) canNotRun = true;
      if (result.hasArchived) hasArchived = true;
      items.push(
        <ListItem disablePadding key={`list-item-${idx}-${spotIdx}-val`}>
          <OverflowTooltipTypography color={Colors.gray1000} variant="body2">
            {result.itemValue || "N/A"}
          </OverflowTooltipTypography>
        </ListItem>,
      );
    });

    const row = (
      <Grid container key={`grid-container-${idx}`}>
        <Grid item md={2} sm={4}>
          <Typography color={Colors.gray1000} fontWeight="bold" variant="body2">
            {getPriorityLevel(idx).text}
          </Typography>
        </Grid>
        <Grid item md={10} sm={8}>
          <List disablePadding>
            {items.length ? (
              items
            ) : (
              <ListItem disablePadding key={`list-item-${idx}-empty`}>
                N/A
              </ListItem>
            )}
          </List>
        </Grid>
      </Grid>
    );

    return { canNotRun, hasArchived, hasDynamicContent, gridRows: [row] };
  };

  const legacy = isLegacyStrategy(main, fallback);
  if (legacy) {
    return (
      <Alert dense severity="warning">
        This strategy is a legacy format. Please archive it and create a new
        one.
      </Alert>
    );
  }

  const mainLoop = formatSpots(0, main);
  const fbLoop = formatSpots(1, fallback);

  const canRun = !(mainLoop.canNotRun || fbLoop.canNotRun);
  const hasArchived = mainLoop.hasArchived || fbLoop.hasArchived;
  const hasDynamicContent = fbLoop.hasDynamicContent;
  const spotEntries = [...mainLoop.gridRows, ...fbLoop.gridRows];

  return (
    <Stack direction="column" spacing={2}>
      <Box>{spotEntries}</Box>

      {!canRun && (
        <Alert dense severity="warning">
          This strategy has missing applications and cannot be deployed.
        </Alert>
      )}

      {hasArchived && (
        <Alert
          dense
          severity="warning"
          title="This strategy contains archived content"
        >
          The content will still play, but you cannot add it to other strategies
        </Alert>
      )}

      {hasDynamicContent && (
        <Alert
          dense
          severity="warning"
          title="This strategy's fallback loop contains dynamic content"
        >
          The content will still play, but you cannot deploy this strategy to
          other devices. Please ensure the fallback loop contains only static
          content such as images or videos.
        </Alert>
      )}
    </Stack>
  );
}
